import React, { useEffect, useState } from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"

const MONO = "var(--app-font-mono)"

const PROJECTS = [
  {
    id: "ai-assistant",
    label: "AI Assistant",
    description: "Dashboard, Chat, Terminal — this app",
    color: "var(--feature-vault)",
    warn: false,
  },
  {
    id: "huts-heroes-dev",
    label: "Hut & Heroes (dev)",
    description: "Idle game — dev branch",
    color: "var(--feature-stats)",
    warn: false,
  },
  {
    id: "huts-heroes",
    label: "Hut & Heroes (production)",
    description: "Idle game — main branch  ⚠ production",
    color: "var(--app-warning)",
    warn: true,
  },
]

export default function DeployProjectDialog({ open, onClose, onSelect }) {
  const [selected, setSelected] = useState("ai-assistant")
  const [title, setTitle] = useState("")
  const [summary, setSummary] = useState("")

  useEffect(() => {
    if (!open) return
    const project = PROJECTS.find((item) => item.id === selected)
    setTitle(`${project?.label ?? "System"} Update`)
    setSummary(`Der aktuelle Stand von ${project?.label ?? "diesem System"} wird gebaut und veröffentlicht.`)
  }, [open, selected])

  const handleConfirm = () => {
    onSelect(selected, { title: title.trim(), summary: summary.trim() })
    onClose()
  }

  const proj = PROJECTS.find((p) => p.id === selected)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: "var(--app-paper)",
          border: "1px solid rgba(168,85,247,0.25)",
          borderRadius: 2,
          minWidth: 360,
        },
      }}
    >
      <DialogTitle sx={{ color: "var(--feature-vault)", fontFamily: MONO, fontSize: "0.85rem", pb: 1, letterSpacing: "0.1em" }}>
        DEPLOY — PROJECT AUSWÄHLEN
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        {PROJECTS.map((p) => {
          const active = selected === p.id
          return (
            <Box
              key={p.id}
              onClick={() => setSelected(p.id)}
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                p: 1.25, mb: 0.75, borderRadius: 1.5, cursor: "pointer",
                border: "1px solid",
                borderColor: active ? `color-mix(in srgb, ${p.color} 40%, transparent)` : "rgba(var(--app-fg-rgb),0.07)",
                backgroundColor: active ? `color-mix(in srgb, ${p.color} 6.7%, transparent)` : "transparent",
                "&:hover": {
                  borderColor: `color-mix(in srgb, ${p.color} 26.7%, transparent)`,
                  backgroundColor: `color-mix(in srgb, ${p.color} 3.1%, transparent)`,
                },
                transition: "all 0.12s",
              }}
            >
              <Box sx={{
                width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                bgcolor: active ? p.color : "rgba(var(--app-fg-rgb),0.15)",
                boxShadow: active ? `0 0 8px ${p.color}` : "none",
                transition: "all 0.15s",
              }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: MONO, fontSize: "0.72rem", color: active ? p.color : "rgba(var(--app-fg-rgb),0.55)", fontWeight: active ? 700 : 400 }}>
                  {p.label}
                </Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: "0.6rem", color: "rgba(var(--app-fg-rgb),0.28)", mt: 0.15 }}>
                  {p.description}
                </Typography>
              </Box>
            </Box>
          )
        })}

        {proj?.warn && (
          <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.25)" }}>
            <Typography sx={{ fontFamily: MONO, fontSize: "0.6rem", color: "var(--app-warning)", lineHeight: 1.5 }}>
              ⚠ Produktions-Deploy — bitte sichergehen dass der dev-Branch stabil ist!
            </Typography>
          </Box>
        )}
        <TextField fullWidth size="small" label="Update-Titel" value={title} onChange={(event) => setTitle(event.target.value)} inputProps={{ maxLength: 100 }} sx={{ mt: 1.3 }} />
        <TextField fullWidth size="small" multiline minRows={2} label="Was wird geändert? (ein kurzer Satz)" value={summary} onChange={(event) => setSummary(event.target.value)} inputProps={{ maxLength: 280 }} sx={{ mt: 1.1 }} />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button
          size="small" variant="outlined" onClick={onClose} fullWidth
          sx={{ fontSize: "0.68rem", fontFamily: MONO, color: "rgba(var(--app-fg-rgb),0.35)", borderColor: "rgba(var(--app-fg-rgb),0.1)", textTransform: "none", "&:hover": { borderColor: "rgba(var(--app-fg-rgb),0.25)" } }}
        >
          Abbrechen
        </Button>
        <Button
          size="small" variant="outlined" onClick={handleConfirm} fullWidth disabled={!summary.trim()}
          sx={{
            fontSize: "0.68rem", fontFamily: MONO, textTransform: "none",
            color: proj?.color ?? "var(--feature-vault)",
            borderColor: `color-mix(in srgb, ${proj?.color ?? "var(--feature-vault)"} 33.3%, transparent)`,
            "&:hover": { borderColor: proj?.color ?? "var(--feature-vault)", bgcolor: `color-mix(in srgb, ${proj?.color ?? "var(--feature-vault)"} 6.3%, transparent)` },
          }}
        >
          Deploy starten
        </Button>
      </DialogActions>
    </Dialog>
  )
}
